import { Paper } from '@material-ui/core';
import { useStyles } from '@webapps/styles';
import { css } from '@emotion/css';
import PropTypes from 'prop-types';
import { useEffect, useState, useCallback } from 'react';
import { getAddress } from '@webapps/business';
import { Autocomplete, FormikTextFieldPx, useMutation } from '@webapps/reactjs-tech-components';
import debounce from 'lodash/debounce';

const FormikAddressPickerNested = ({
  formik,
  name = "address",
  pathValue,
  defaultValue = "",
  multiple = false,
  endAdornment,
  placeholder = "Rechercher une adresse",
  completed,
  borderRadius = 400,
  ...props
}) => {
  const { setFieldValue, values } = formik;
  const { theme: { fontSizes } } = useStyles();

  // Use a flat value for Autocomplete, sync with Formik's nested state
  const [inputValue, setInputValue] = useState(values?.[name]?.label || defaultValue?.label || '');
  const [addresses, setAddresses] = useState([]);

  // Sync inputValue with Formik's values[name].label
  useEffect(() => {
    const currentLabel = values?.[name]?.label || '';
    if (currentLabel !== inputValue) {
      setInputValue(currentLabel);
    }
  }, [values?.[name]?.label, inputValue]);

  const getAddressInfo = useMutation(getAddress, {
    onSuccess: (result) => {
      const features = result?.data?.features || [];
      setAddresses(features);

      const first = features[0]?.properties;
      if (first) {
        setFieldValue('address.city', first.city);
        setFieldValue('address.cityCode', first.postcode);
      }
    },
  });


  const debouncedFetchAddresses = useCallback(
    debounce(async (value) => {
      await getAddressInfo.mutateAsync({ name: value });
    }, 500),
    [getAddressInfo]
  );

  
  const handleInputChange = async (_, value) => {
    setInputValue(value);
    setFieldValue(name, {
      label: value,
      city: values?.[name]?.city || '',
      cityCode: values?.[name]?.cityCode || ''
    });

    if (value.length >= 3) {
      debouncedFetchAddresses(value);
    } else {
      setAddresses([]);
      debouncedFetchAddresses.cancel();
    }
  };

  const options = addresses.map(option => option?.properties?.label || '');

  return (
    <Autocomplete
      autoComplete={false}
      multiple={multiple}
      freeSolo={false}
      className={css({
        '.MuiInputBase-root': {
          borderRadius: borderRadius,
        },
      })}
      options={options}
      value={values?.[name]?.label || ''}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={(option) => option || ''}
      getOptionSelected={(option, value) => option === value}
      popupIcon={endAdornment || null}
      PaperComponent={({ children }) => (
        <Paper
          style={{
            '@media only screen and (max-width:1920px)': { fontSize: '13.5px' },
            '@media only screen and (max-width:1600px)': { fontSize: '13.5px' },
          }}
        >
          {children}
        </Paper>
      )}
      renderInput={(params) => (
        <FormikTextFieldPx
          {...params}
          name={`${name}.label`} // Match Formik field name
          formik={formik}
          completed={completed}
          borderRadius={borderRadius}
          placeholder={placeholder}
        />
      )}
      {...props}
    />
  );
};

FormikAddressPickerNested.propTypes = {
  name: PropTypes.string,
  formik: PropTypes.object.isRequired,
  pathValue: PropTypes.string,
  defaultValue: PropTypes.any,
  multiple: PropTypes.bool,
  endAdornment: PropTypes.node,
  completed: PropTypes.bool,
  borderRadius: PropTypes.number,
};

export default FormikAddressPickerNested;